import type { UploadedFontCssSource } from 'infrastructure/currentTenant/uploadedFonts';
import { isCardThemeHeadingPlatformDefaultFont } from '../data/cardThemeFonts';
import type { CardPreviewTarget, CardThemeTextStyle } from '../data/types';

export const IOS_CARD_PREVIEW_FONT_FAMILY_FALLBACK =
  "'SFProText-Regular', -apple-system, BlinkMacSystemFont, " +
  "'Helvetica Neue', arial, sans-serif";
export const ANDROID_CARD_PREVIEW_FONT_FAMILY_FALLBACK =
  "'Roboto', 'Noto Sans', arial, sans-serif";

const CARD_PREVIEW_BUNDLED_FONT_BASE_URL = '/fonts/card-preview';

interface CardPreviewBundledTenantFontGroup {
  fonts: UploadedFontCssSource[];
  tenants: string[];
}

type CardPreviewBundledTenantFontGroups = CardPreviewBundledTenantFontGroup[];

const normalizeFontFamily = (fontFamily: string) =>
  fontFamily.trim().toLowerCase();

const normalizeTenantName = (tenantName: string) =>
  tenantName.trim().toLowerCase();

const getFontUrl = (directory: string, fileName: string) =>
  `${CARD_PREVIEW_BUNDLED_FONT_BASE_URL}/${directory}/${fileName}`;

const createBundledFont = (
  family: string,
  directory: string,
  fileName: string
): UploadedFontCssSource => ({
  family,
  url: getFontUrl(directory, fileName),
});

export const CARD_PREVIEW_BUNDLED_GLOBAL_FONTS: UploadedFontCssSource[] = [
  createBundledFont('Roboto', 'common', 'Roboto-Regular.woff2'),
  createBundledFont('SFProText-Regular', 'common', 'SFProText-Regular.woff2'),
  createBundledFont('SFProText-Semibold', 'common', 'SFProText-Semibold.woff2'),
];

const BUNDLED_TENANT_FONT_GROUPS: CardPreviewBundledTenantFontGroups = [
  {
    tenants: ['nba', 'nbaqa', 'nbademo', 'stormideasqa'],
    fonts: [
      createBundledFont(
        'Knockout-67FullBantamwt',
        'nba',
        'Knockout-67FullBantamwt.woff2'
      ),
      createBundledFont('knockout_67', 'nba', 'Knockout-67FullBantamwt.woff2'),
      createBundledFont(
        'roboto_condensed_semibold',
        'nba',
        'RobotoCondensed-SemiBold.woff2'
      ),
    ],
  },
  {
    tenants: ['celticfc', 'celticfcqa', 'stormideasqa'],
    fonts: [
      createBundledFont(
        'NormalidadExtended-Bold',
        'celtic',
        'NormalidadExtended-Bold.woff2'
      ),
      createBundledFont('OpenSans-Regular', 'celtic', 'OpenSans-Regular.woff2'),
    ],
  },
];

const CARD_PREVIEW_SYSTEM_FONT_ALIASES = new Set([
  'roboto',
  'san francisco',
  'sf pro',
  'sf pro text',
]);

const TENANT_RESTRICTED_FONT_FAMILIES = new Map<string, Set<string>>();

BUNDLED_TENANT_FONT_GROUPS.forEach(({ fonts, tenants }) => {
  const allowedTenants = new Set(tenants.map(normalizeTenantName));

  fonts.forEach(font => {
    const fontFamily = font.title?.trim() || font.family?.trim();

    if (fontFamily) {
      TENANT_RESTRICTED_FONT_FAMILIES.set(
        normalizeFontFamily(fontFamily),
        allowedTenants
      );
    }
  });
});

export const getCardPreviewConfiguredPlatformFontFamily = (
  textStyle: CardThemeTextStyle | null | undefined,
  previewTarget: CardPreviewTarget
) => {
  const fontFamily =
    previewTarget === 'android'
      ? textStyle?.androidFont || textStyle?.font
      : textStyle?.iOSFont || textStyle?.font;

  return fontFamily?.trim() || undefined;
};

const getEscapedFontFamily = (fontFamily: string) =>
  fontFamily.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const isTenantAllowedForRestrictedFont = (
  fontFamily: string,
  tenantName?: string | null
) => {
  const allowedTenants = TENANT_RESTRICTED_FONT_FAMILIES.get(
    normalizeFontFamily(fontFamily)
  );

  if (!allowedTenants) {
    return true;
  }

  if (!tenantName) {
    return false;
  }

  return allowedTenants.has(normalizeTenantName(tenantName));
};

export const getCardPreviewPlatformFontFallback = (
  previewTarget: CardPreviewTarget
) =>
  previewTarget === 'android'
    ? ANDROID_CARD_PREVIEW_FONT_FAMILY_FALLBACK
    : IOS_CARD_PREVIEW_FONT_FAMILY_FALLBACK;

export const getCardPreviewBundledTenantFonts = (
  tenantName?: string | null
) => {
  const normalizedTenantName = normalizeTenantName(tenantName || '');

  if (!normalizedTenantName) {
    return [];
  }

  return BUNDLED_TENANT_FONT_GROUPS.filter(({ tenants }) =>
    tenants.map(normalizeTenantName).includes(normalizedTenantName)
  ).flatMap(({ fonts }) => fonts);
};

export const getCardPreviewBundledFonts = (tenantName?: string | null) => [
  ...CARD_PREVIEW_BUNDLED_GLOBAL_FONTS,
  ...getCardPreviewBundledTenantFonts(tenantName),
];

export const getCardPreviewThemeFontFamily = (
  textStyle: CardThemeTextStyle | null | undefined,
  previewTarget: CardPreviewTarget,
  tenantName?: string | null
) => {
  const fontFamily = getCardPreviewConfiguredPlatformFontFamily(
    textStyle,
    previewTarget
  );

  if (!fontFamily) {
    return undefined;
  }

  const normalizedFontFamily = normalizeFontFamily(fontFamily);

  if (
    isCardThemeHeadingPlatformDefaultFont(normalizedFontFamily) ||
    CARD_PREVIEW_SYSTEM_FONT_ALIASES.has(normalizedFontFamily) ||
    !isTenantAllowedForRestrictedFont(fontFamily, tenantName)
  ) {
    return undefined;
  }

  return fontFamily;
};

export const getCardPreviewResolvedFontFamily = (
  textStyle: CardThemeTextStyle | null | undefined,
  previewTarget: CardPreviewTarget,
  tenantName?: string | null
): string | undefined => {
  const platformFallback = getCardPreviewPlatformFontFallback(previewTarget);
  const fontFamily = getCardPreviewThemeFontFamily(
    textStyle,
    previewTarget,
    tenantName
  );

  if (!fontFamily) {
    return textStyle ? platformFallback : undefined;
  }

  const quotedFontFamily = `'${getEscapedFontFamily(fontFamily)}'`;

  if (platformFallback.startsWith(quotedFontFamily)) {
    return platformFallback;
  }

  return `${quotedFontFamily}, ${platformFallback}`;
};
